// v3.15-1135C — Admin-only Reminder Automation kill-switch settings.
// Toggles for Client + Rep appointment reminder emails. Defaults to OFF.
// When disabled: SMTP is skipped but in-app notifications + audit logging
// + DB stamping continue to run normally.
import { useEffect, useState } from 'react';
import { Bell, AlertTriangle, ShieldAlert, Loader2, CheckCircle2 } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

export default function AppointmentReminderSettingsPage() {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [clientEnabled, setClientEnabled] = useState(false);
  const [repEnabled, setRepEnabled]       = useState(false);
  const [meta, setMeta]         = useState({ updated_at: null, updated_by: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/settings/appointment-reminders');
        if (cancelled) return;
        setClientEnabled(!!res.data.client_enabled);
        setRepEnabled(!!res.data.rep_enabled);
        setMeta({ updated_at: res.data.updated_at, updated_by: res.data.updated_by });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load reminder settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/settings/appointment-reminders', {
        client_enabled: clientEnabled,
        rep_enabled:    repEnabled,
      });
      setMeta({ updated_at: res.data.updated_at, updated_by: res.data.updated_by });
      toast.success('Reminder settings updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500"
           data-testid="reminder-settings-loading">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading reminder settings…
      </div>
    );
  }

  const bothOff = !clientEnabled && !repEnabled;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="reminder-settings-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-indigo-50 p-2.5">
          <Bell className="h-6 w-6 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Appointment Reminder Settings</h1>
          <p className="text-sm text-slate-500 mt-1">
            Master kill switches for outbound reminder emails. In-app notifications,
            audit logging and DB stamping are <strong>always on</strong>, regardless of these toggles.
          </p>
        </div>
      </div>

      {/* Investigation banner */}
      {bothOff && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex gap-3"
             data-testid="kill-switch-active-banner">
          <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">Kill switch active — all reminder emails are OFF.</div>
            <div className="mt-1">
              No appointment reminder emails will be sent to clients or reps until at least one
              toggle is turned ON below. In-app reminders and audit logs continue to flow normally.
            </div>
          </div>
        </div>
      )}

      {/* Client toggle */}
      <div className="rounded-lg border border-slate-200 bg-white p-5"
           data-testid="client-reminder-row">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label htmlFor="client-toggle" className="text-base font-semibold text-slate-900">
              Client Reminder Automation
            </Label>
            <p className="text-sm text-slate-500 mt-1">
              Sends 24h and 1h appointment reminder emails to the <strong>client</strong>.
            </p>
            <p className="text-xs mt-2 flex items-center gap-1.5">
              {clientEnabled
                ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /><span className="text-emerald-700 font-medium">Enabled — emails will be sent.</span></>
                : <><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /><span className="text-amber-700 font-medium">Disabled — no client emails will be sent.</span></>}
            </p>
          </div>
          <Switch
            id="client-toggle"
            checked={clientEnabled}
            onCheckedChange={setClientEnabled}
            data-testid="client-reminder-toggle"
          />
        </div>
      </div>

      {/* Rep toggle */}
      <div className="rounded-lg border border-slate-200 bg-white p-5"
           data-testid="rep-reminder-row">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label htmlFor="rep-toggle" className="text-base font-semibold text-slate-900">
              Rep Reminder Automation
            </Label>
            <p className="text-sm text-slate-500 mt-1">
              Sends 1h, 30m, 15m and Missed reminder emails to the <strong>assigned rep</strong>.
              In-app Hub notifications continue regardless of this toggle.
            </p>
            <p className="text-xs mt-2 flex items-center gap-1.5">
              {repEnabled
                ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /><span className="text-emerald-700 font-medium">Enabled — emails will be sent.</span></>
                : <><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /><span className="text-amber-700 font-medium">Disabled — no rep emails (in-app still fires).</span></>}
            </p>
          </div>
          <Switch
            id="rep-toggle"
            checked={repEnabled}
            onCheckedChange={setRepEnabled}
            data-testid="rep-reminder-toggle"
          />
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center justify-between pt-2">
        <div className="text-xs text-slate-500">
          {meta.updated_at
            ? <>Last updated {new Date(meta.updated_at).toLocaleString()} by <strong>{meta.updated_by || 'unknown'}</strong></>
            : 'Never updated — using defaults (both OFF).'}
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="reminder-settings-save-btn">
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : 'Save Settings'}
        </Button>
      </div>

      {/* Audit reminder */}
      <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
        Every change is written to the system activity log
        (<code>appointment_reminder_settings_changed</code>) with the previous and new
        state and the admin who toggled it.
      </div>
    </div>
  );
}
